import { Send, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface Photo {
  id: string
  photoTitle: string
  photoUrl: string
}

interface Selections {
  first: string | null
  second: string | null
  third: string | null
}

interface VotingControlsProps {
  photos: Photo[]
  selections: Selections
  currentPhotoId?: string
  onVote: (photoId: string, rank: 1 | 2 | 3) => void
  onClearSelection: (rank: 1 | 2 | 3) => void
  onPhotoClick?: (photoId: string) => void
  canSubmit: boolean
  isSubmitting: boolean
  onSubmit: () => void
  variant?: 'horizontal' | 'stacked' | 'modal'
}

const labels = { 1: '1st', 2: '2nd', 3: '3rd' } as const
const borderColors = {
  1: 'border-[#FFD700]', // Gold
  2: 'border-[#C0C0C0]', // Silver
  3: 'border-[#CD7F32]', // Bronze
} as const

const ringColors = {
  1: 'ring-[#FFD700]', // Gold
  2: 'ring-[#C0C0C0]', // Silver
  3: 'ring-[#CD7F32]', // Bronze
} as const

// Shared slot rendering for both modal and horizontal variants
interface SelectionSlotProps {
  rank: 1 | 2 | 3
  photo: Photo | undefined
  isHighlighted?: boolean
  onClick?: () => void
  onClear?: () => void
  theme: 'dark' | 'light'
  compact?: boolean // Horizontal layout: thumbnail | rank + title + X
}

function SelectionSlot({ rank, photo, isHighlighted, onClick, onClear, theme, compact }: SelectionSlotProps) {
  const label = labels[rank]
  const isDark = theme === 'dark'

  const borderClass = photo ? borderColors[rank] : isDark ? 'border-dashed border-white/20' : 'border-border'

  const bgClass = isHighlighted
    ? `bg-white/20 ${borderColors[rank]} ring-4 ${ringColors[rank]}`
    : photo
      ? isDark
        ? `bg-white/10 ${borderColors[rank]}`
        : `${borderColors[rank]} bg-accent/10`
      : isDark
        ? 'bg-white/5'
        : 'bg-surface-hover'

  // Compact horizontal layout for list view and modal
  if (compact) {
    return (
      <div className="relative min-w-0 flex-1">
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClick?.()
            }
          }}
          className={`xs:justify-start xs:p-2 flex cursor-pointer items-center justify-around gap-2 rounded-xl border-2 p-1.5 transition-all ${bgClass} ${!isHighlighted && photo ? borderClass : ''} `}
        >
          {photo ? (
            <>
              {/* Thumbnail - expands on xs, fixed on xs+ */}
              <div
                className={`xs:flex-none xs:w-12 xs:h-12 flex h-12 max-w-16 flex-1 items-center justify-center overflow-hidden rounded-lg ${isDark ? 'bg-black/20' : 'bg-surface-hover'}`}
              >
                <img src={photo.photoUrl} alt={photo.photoTitle} className="max-h-full max-w-full object-contain" />
              </div>
              {/* Info column - xs+ */}
              <div className="xs:flex hidden min-w-0 flex-1 flex-col overflow-hidden">
                <span className={`text-[10px] ${isDark ? 'text-white/60' : 'text-foreground-subtle'}`}>{label}</span>
                <span className={`block truncate text-xs ${isDark ? 'text-white' : 'text-foreground'}`}>
                  {photo.photoTitle}
                </span>
              </div>
              {/* Clear button */}
              {onClear && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onClear()
                  }}
                  title="Clear selection"
                  className="bg-error/20 text-error hover:bg-error hover:text-error-foreground shrink-0 rounded-full p-1.5 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          ) : (
            <div className="xs:px-3 flex h-12 w-full items-center justify-center px-2">
              <span className={`text-xs ${isDark ? 'text-white/40' : 'text-foreground-subtle'}`}>{label}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Vertical stacked layout for modal
  return (
    <div className="relative min-w-[80px] flex-1 sm:min-w-[100px]">
      <button
        onClick={onClick}
        className={`flex w-full flex-col items-center gap-1 rounded-xl border-2 p-2 transition-all sm:p-3 ${bgClass} ${!isHighlighted && photo ? borderClass : ''} `}
      >
        {photo ? (
          <>
            <div
              className={`flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg sm:h-16 sm:w-16 ${isDark ? 'bg-black/20' : 'bg-surface-hover'}`}
            >
              <img src={photo.photoUrl} alt={photo.photoTitle} className="max-h-full max-w-full object-contain" />
            </div>
            <span className={`text-[10px] ${isDark ? 'text-white/60' : 'text-foreground-subtle'}`}>{label}</span>
            <span
              className={`max-w-[70px] truncate text-xs sm:max-w-[90px] ${isDark ? 'text-white' : 'text-foreground'}`}
            >
              {photo.photoTitle}
            </span>
            {onClear && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onClear()
                }}
                title="Clear selection"
                className="bg-error/20 text-error hover:bg-error hover:text-error-foreground mt-1 rounded-full p-1 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </>
        ) : (
          <div className="flex h-14 w-14 flex-col items-center justify-center sm:h-16 sm:w-16">
            <span className={`text-xs ${isDark ? 'text-white/40' : 'text-foreground-subtle'}`}>{label}</span>
            <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-foreground-subtle/60'}`}>
              Tap to select
            </span>
          </div>
        )}
      </button>
    </div>
  )
}

export function VotingControls({
  photos,
  selections,
  currentPhotoId,
  onVote,
  onClearSelection,
  onPhotoClick,
  canSubmit,
  isSubmitting,
  onSubmit,
  variant = 'horizontal',
}: VotingControlsProps) {
  const getSelectedPhoto = (rank: 1 | 2 | 3): Photo | undefined => {
    const rankKey = rank === 1 ? 'first' : rank === 2 ? 'second' : 'third'
    const selectedId = selections[rankKey]
    return selectedId ? photos.find((p) => p.id === selectedId) : undefined
  }

  const getSelectedId = (rank: 1 | 2 | 3): string | null => {
    const rankKey = rank === 1 ? 'first' : rank === 2 ? 'second' : 'third'
    return selections[rankKey]
  }

  // Get rank of current photo (if selected)
  const getCurrentPhotoRank = (): 1 | 2 | 3 | null => {
    if (!currentPhotoId) return null
    if (selections.first === currentPhotoId) return 1
    if (selections.second === currentPhotoId) return 2
    if (selections.third === currentPhotoId) return 3
    return null
  }

  // Modal variant - same compact layout as list view, dark theme
  if (variant === 'modal') {
    const currentRank = getCurrentPhotoRank()

    return (
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        {/* 3 selection slots - same as list view */}
        <div className="flex flex-1 gap-2">
          {([1, 2, 3] as const).map((rank) => (
            <SelectionSlot
              key={rank}
              rank={rank}
              photo={getSelectedPhoto(rank)}
              isHighlighted={currentRank === rank}
              onClick={() => currentPhotoId && onVote(currentPhotoId, rank)}
              theme="dark"
              compact
            />
          ))}
        </div>

        {/* Submit button */}
        <Button
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
          isLoading={isSubmitting}
          size="lg"
          className="w-full md:w-auto"
        >
          <Send className="h-5 w-5" />
          Submit Vote
        </Button>
      </div>
    )
  }

  // Horizontal variant (default) - for bottom bar (light background)
  // Desktop (md+): compact 1,2,3 cards on left, button on right (natural flow)
  // Mobile: stacked layout with button below
  return (
    <div className="flex flex-col gap-3 overflow-hidden md:flex-row md:items-center">
      {/* 3 selection slots - expand to fill available space */}
      <div className="flex min-w-0 flex-1 gap-2">
        {([1, 2, 3] as const).map((rank) => (
          <SelectionSlot
            key={rank}
            rank={rank}
            photo={getSelectedPhoto(rank)}
            onClick={() => {
              const photoId = getSelectedId(rank)
              if (photoId) onPhotoClick?.(photoId)
            }}
            onClear={() => onClearSelection(rank)}
            theme="light"
            compact
          />
        ))}
      </div>

      {/* Submit button - below on mobile, right on md+ */}
      <Button
        onClick={onSubmit}
        disabled={!canSubmit || isSubmitting}
        isLoading={isSubmitting}
        size="lg"
        className="w-full md:w-auto"
      >
        <Send className="h-5 w-5" />
        Submit Vote
      </Button>
    </div>
  )
}
